<template>
    <!--
      The batch dropdown that sits beside a stock picker.

      Renders nothing when the selected stock item holds no batches, so a movement on unbatched stock
      looks and behaves exactly as it did before. When it does hold batches the caller must not let the
      row be added until `modelValue` is set - `required` is what it reads to know that.
    -->
    <Dropdown
        v-if="hasBatches"
        class="batch-select"
        :modelValue="modelValue"
        :options="batches"
        :optionLabel="batchLabel"
        :placeholder="placeholder"
        :editable="false"
        style="width: 100%; display: flex"
        @update:modelValue="$emit('update:modelValue', $event)"
    />
</template>

<script>
import Dropdown from 'primevue/dropdown';
import { batchOptionLabel } from '@/services/stockItemDisplay';

export default {
    name: 'BatchSelect',
    components: { Dropdown },
    // stockItem is the row the medication picker returned. modelValue is the chosen batch object, so
    // the parent can read `uuid` off it for the payload and `batch_name` for display.
    props: {
        stockItem: { type: Object, default: null },
        modelValue: { type: Object, default: null }
    },
    emits: ['update:modelValue'],
    computed: {
        // The key is absent when the endpoint did not eager-load the relation, which reads the same as
        // holding no batches: no selection needed.
        batches() {
            return (this.stockItem && Array.isArray(this.stockItem.batches)) ? this.stockItem.batches : [];
        },
        // Second line only. The backend already leaves the batch list out of the payload for a facility
        // without batch tracking, and its validation makes the same check before requiring one - so this
        // is here in case a stale response still carries batches, not as the gate.
        batchFeaturesActive() {
            return this.$store.getters.isBatchTrackingEnabled;
        },
        hasBatches() {
            return this.batchFeaturesActive && this.batches.length > 0;
        },
        placeholder() {
            return 'Select Batch';
        }
    },
    watch: {
        // A batch belongs to one stock item, so a selection must never survive the medication changing.
        stockItem() {
            if (this.modelValue) {
                this.$emit('update:modelValue', null);
            }
        }
    },
    methods: {
        batchLabel(batch) {
            return batchOptionLabel(batch);
        }
    }
};
</script>

<style scoped>
.batch-select {
    margin-left: 10px;
}
</style>
